import { useState } from "react";

interface LogoutButtonProps {
  onLogout: () => Promise<void>;
  className?: string;
}

/**
 * The one control that ends a session (feature/auth task #6). Deliberately plain
 * text rather than a new icon — this app's icon set is a fixed, hand-drawn eleven
 * (see Icon.tsx's remarks on why); adding a twelfth for one button is not worth
 * the inconsistency risk a rushed glyph would bring.
 */
export function LogoutButton({ onLogout, className }: LogoutButtonProps) {
  const [pending, setPending] = useState(false);

  async function handleClick(): Promise<void> {
    setPending(true);
    try {
      await onLogout();
      // On success the parent re-renders the login form; this component unmounts.
    } catch {
      // Logout is deliberately best-effort client-side: even if the network call
      // fails, there is nothing more useful to do than let the user try again —
      // it must never trap them on a page they were trying to leave.
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      className={`btn btn--ghost${className ? ` ${className}` : ""}`}
      onClick={() => void handleClick()}
      disabled={pending}
    >
      {pending ? "Signing out…" : "Log out"}
    </button>
  );
}
